import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, onSnapshot, serverTimestamp, query, where, arrayUnion, arrayRemove,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, storage } from "../firebase";
import { AppDispatch } from "./store";

/** Firestore collection and Storage folder for products. Change them here only. */
const DB_COLLECTION = "products";
const STORAGE_FOLDER = "products"; // products/{ownerId}/{productId}/...

/** One color of a product: its own photos and its own stock per size. */
export interface ProductVariant {
  color: string;
  hex?: string | null;
  images: string[];
  stock: { size: string; qty: number }[]; // size "-" = the category has no sizes
}

/* products/{productId}  (public read, owner write) */
export interface Product {
  productId: string;
  ownerId: string;
  name: string;
  price: number;          // EUR
  quantity: number;       // total stock (sum of every color and size when variants exist)
  images: string[];       // main photos, not tied to a color
  description?: string;
  categoryId?: string;    // Attributes.tsx category id, e.g. "00801"
  subcategoryId?: string; // optional deeper category id
  gender?: string[];      // GENDERS ids: "01" man, "02" woman, "03" kid
  attributes?: Record<string, string | string[]>; // e.g. { Size: ["S","M"], Color: ["Black","Navy"], Fit: "Slim" }
  variants?: ProductVariant[];                    // one entry per chosen color
  flavor?: string;
  unit?: string;
  country?: string | null; // ships from / sold in; empty = everywhere
  city?: string | null;
  published?: boolean;     // false hides it; missing counts as published
  createdAt?: number | null;
}

export interface ProductFilters { city: string | null; country: string | null; }

interface ProductState {
  products: Product[];
  ownerProducts: Product[];          // products where ownerId == the signed-in user's uid (drafts included)
  ownerProductsLoading: boolean;
  filteredProducts: Product[];
  filters: ProductFilters;
  status: "idle" | "loading" | "failed";
  error: string | null;
}

const initialState: ProductState = {
  products: [], ownerProducts: [], ownerProductsLoading: false, filteredProducts: [], filters: { city: null, country: null }, status: "idle", error: null,
};


const isLive = (p: Product) => p.published !== false;

const applyFilters = (s: ProductState) => {
  const { country, city } = s.filters;
  s.filteredProducts = s.products.filter(
    (p) => (!country || !p.country || p.country === country) && (!city || !p.city || p.city === city)
  );
};

const fromDoc = (d: any): Product => {
  const data = d.data();
  return { ...data, productId: d.id, createdAt: data.createdAt?.toMillis?.() ?? null } as Product;
};

/** Every photo url a product owns: main photos plus each color's photos. */
const allImageUrls = (data: any): string[] => [
  ...(data?.images || []),
  ...((data?.variants || []) as ProductVariant[]).flatMap((v) => v?.images || []),
];

/* ───────────── THUNKS ───────────── */

/** Live listener for every visible product. Returns the unsubscribe function. */
export const listenToPublishedProducts = () => (dispatch: AppDispatch) =>
  onSnapshot(
    query(collection(db, DB_COLLECTION), where("published", "==", true)),
    (snap) => dispatch(setProducts(snap.docs.map(fromDoc))),
    (e) => dispatch(setProductsError(e.message))
  );

export const fetchAllPublishedProducts = createAsyncThunk<Product[], void, { rejectValue: string }>(
  "product/fetchAllPublishedProducts",
  async (_, { rejectWithValue }) => {
    try {
      const snap = await getDocs(collection(db, DB_COLLECTION));
      return snap.docs.map(fromDoc).filter(isLive);
    } catch (err: any) { return rejectWithValue(err.message); }
  }
);

/** Live feed of the signed-in owner's own products (drafts included). ownerId == auth uid. */
let ownerFeedUnsub: (() => void) | null = null;

export const listenToOwnerProductsFeed = (ownerId?: string) => (dispatch: AppDispatch) => {
  const uid = ownerId || auth.currentUser?.uid;
  if (ownerFeedUnsub) { ownerFeedUnsub(); ownerFeedUnsub = null; }
  if (!uid) return () => {};
  dispatch(setOwnerProductsLoading(true));
  ownerFeedUnsub = onSnapshot(
    query(collection(db, DB_COLLECTION), where("ownerId", "==", uid)),
    (snap) => dispatch(setOwnerProducts(snap.docs.map(fromDoc).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)))),
    (e) => { dispatch(setProductsError(e.message)); dispatch(setOwnerProductsLoading(false)); }
  );
  return () => { if (ownerFeedUnsub) { ownerFeedUnsub(); ownerFeedUnsub = null; } };
};

export const stopProductsFeed = () => (dispatch: AppDispatch) => {
  if (ownerFeedUnsub) { ownerFeedUnsub(); ownerFeedUnsub = null; }
  dispatch(setOwnerProductsLoading(false));
};

/** Create (no productId) or update (productId) a product. ownerId is always the signed-in uid. */
export const upsertProduct = createAsyncThunk(
  "product/upsertProduct",
  async (p: { productId?: string; data: Partial<Omit<Product, "productId" | "ownerId">> }, { rejectWithValue }) => {
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not logged in");
      const ref = p.productId ? doc(db, DB_COLLECTION, p.productId) : doc(collection(db, DB_COLLECTION));
      const data = { ...p.data, productId: ref.id, ownerId: u.uid };
      if (!p.productId) {
        const fresh = { published: true, images: [] as string[], ...data };
        await setDoc(ref, { ...fresh, createdAt: serverTimestamp() });
        return fresh as Partial<Product> & { productId: string; ownerId: string };
      }
      await setDoc(ref, data, { merge: true });
      return data as Partial<Product> & { productId: string; ownerId: string };
    } catch (err: any) { return rejectWithValue(err.message); }
  }
);

/** Upload photos to products/{ownerId}/{productId}/... and add their URLs to the product's images. */
export const upsertProductMedia = createAsyncThunk(
  "product/upsertProductMedia",
  async (p: { productId: string; files: File[] }, { rejectWithValue }) => {
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not logged in");
      const downloadURLs: string[] = [];
      for (const file of p.files) {
        const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const r = storageRef(storage, `${STORAGE_FOLDER}/${u.uid}/${p.productId}/${Date.now()}-${safe}`);
        await uploadBytes(r, file, { contentType: file.type });
        downloadURLs.push(await getDownloadURL(r));
      }
      await updateDoc(doc(db, DB_COLLECTION, p.productId), { images: arrayUnion(...downloadURLs) });
      return { productId: p.productId, downloadURLs };
    } catch (err: any) { return rejectWithValue(err.message); }
  }
);

/** Upload files to Storage and return their URLs. Does NOT touch the product's `images`
    (used for per-color photos, which are saved inside `variants`).
    `tag` (the color name) goes into the file name, so the path stays products/{ownerId}/{productId}/{file}. */
export const uploadProductFiles = createAsyncThunk<
  string[],
  { productId: string; files: File[]; tag?: string },
  { rejectValue: string }
>("product/uploadProductFiles", async ({ productId, files, tag }, { rejectWithValue }) => {
  try {
    const u = auth.currentUser;
    if (!u) throw new Error("Not logged in");
    const slug = (tag || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 24);
    const urls: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const r = storageRef(storage, `${STORAGE_FOLDER}/${u.uid}/${productId}/${slug ? slug + "-" : ""}${Date.now()}-${i}-${safe}`);
      await uploadBytes(r, file, { contentType: file.type });
      urls.push(await getDownloadURL(r));
    }
    return urls;
  } catch (err: any) { return rejectWithValue(err.message); }
});

/** Delete files from Storage by download url. Missing files are ignored. */
export const deleteProductFiles = createAsyncThunk<void, string[]>(
  "product/deleteProductFiles",
  async (urls) => {
    await Promise.all(urls.map((u) => deleteObject(storageRef(storage, u)).catch(() => {})));
  }
);

/** Remove one photo from a product and from Storage. */
export const deleteProductMedia = createAsyncThunk(
  "product/deleteProductMedia",
  async (p: { productId: string; photoUrl: string }, { rejectWithValue }) => {
    try {
      await updateDoc(doc(db, DB_COLLECTION, p.productId), { images: arrayRemove(p.photoUrl) });
      try { await deleteObject(storageRef(storage, p.photoUrl)); } catch { /* already gone */ }
      return p;
    } catch (err: any) { return rejectWithValue(err.message); }
  }
);

/** Delete a product and every photo it owns (main photos and each color's photos). */
export const deleteProduct = createAsyncThunk("product/deleteProduct", async (productId: string, { rejectWithValue }) => {
  try {
    const ref = doc(db, DB_COLLECTION, productId);
    const snap = await getDoc(ref);
    const urls: string[] = snap.exists() ? allImageUrls(snap.data()) : [];
    await deleteDoc(ref);
    await Promise.all(urls.map((url) => deleteObject(storageRef(storage, url)).catch(() => {})));
    return productId;
  } catch (err: any) { return rejectWithValue(err.message); }
});

/* ───────────── SLICE ───────────── */

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setProducts: (s, a: PayloadAction<Product[]>) => { s.products = a.payload; s.status = "idle"; s.error = null; applyFilters(s); },
    setOwnerProducts: (s, a: PayloadAction<Product[]>) => { s.ownerProducts = a.payload; s.ownerProductsLoading = false; },
    setOwnerProductsLoading: (s, a: PayloadAction<boolean>) => { s.ownerProductsLoading = a.payload; },
    setProductsError: (s, a: PayloadAction<string>) => { s.status = "failed"; s.error = a.payload; },
    setFilters: (s, a: PayloadAction<Partial<ProductFilters>>) => { s.filters = { ...s.filters, ...a.payload }; applyFilters(s); },
    /** null = show everything; otherwise products sold in that country (or with no country set). */
    filterLocalProductsByCountry: (s, a: PayloadAction<string | null>) => { s.filters.country = a.payload; applyFilters(s); },
  },
  extraReducers: (b) => {
    b.addCase(fetchAllPublishedProducts.pending, (s) => { s.status = "loading"; })
      .addCase(fetchAllPublishedProducts.fulfilled, (s, a) => { s.products = a.payload; s.status = "idle"; applyFilters(s); })
      .addCase(fetchAllPublishedProducts.rejected, (s, a) => { s.status = "failed"; s.error = a.payload || "Couldn't load products"; })
      .addCase(upsertProduct.fulfilled, (s, a) => {
        const patch = (list: Product[]) => {
          const i = list.findIndex((p) => p.productId === a.payload.productId);
          if (i >= 0) list[i] = { ...list[i], ...a.payload } as Product; else list.unshift(a.payload as Product);
        };
        patch(s.ownerProducts);
        if (a.payload.published !== false) patch(s.products);
        applyFilters(s);
      })
      .addCase(upsertProductMedia.fulfilled, (s, a) => {
        const add = (list: Product[]) => { const p = list.find((x) => x.productId === a.payload.productId); if (p) p.images = [...(p.images || []), ...a.payload.downloadURLs]; };
        add(s.ownerProducts); add(s.products); applyFilters(s);
      })
      .addCase(deleteProductMedia.fulfilled, (s, a) => {
        const rm = (list: Product[]) => { const p = list.find((x) => x.productId === a.payload.productId); if (p) p.images = (p.images || []).filter((u) => u !== a.payload.photoUrl); };
        rm(s.ownerProducts); rm(s.products); applyFilters(s);
      })
      .addCase(deleteProduct.fulfilled, (s, a) => {
        s.products = s.products.filter((p) => p.productId !== a.payload);
        s.ownerProducts = s.ownerProducts.filter((p) => p.productId !== a.payload);
        applyFilters(s);
      });
  },
});

export const { setProducts, setOwnerProducts, setOwnerProductsLoading, setProductsError, setFilters, filterLocalProductsByCountry } = productSlice.actions;
export default productSlice.reducer;